import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { PageShell } from "@/components/site-chrome";
import { Globe, Award, Shield, Newspaper, Users, Mail } from "lucide-react";

export const Route = createFileRoute("/about")({
  component: AboutPage,
});

function AboutPage() {
  useEffect(() => {
    document.title = "About Weborbis.top — Football News & Sports Information";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", "Weborbis.top is an independently operated football news and sports information platform providing the latest match updates, football news, team information, player insights, competition analysis, and trending stories.");
    }
  }, []);

  return (
    <PageShell>
      <div className="relative overflow-hidden bg-ink text-white py-16 md:py-20">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
        </div>
        <div className="container-page relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">
              About Weborbis.top
            </h1>
            <p className="mt-4 text-lg text-white/80">
              Your independent source for football news and sports information
            </p>
          </div>
        </div>
      </div>

      <div className="container-page py-12 md:py-16">
        <div className="grid gap-8 md:grid-cols-3">
          <div className="flex items-start gap-4 p-6 bg-muted/50 rounded-xl">
            <div className="p-3 bg-brand rounded-lg shrink-0">
              <Users className="w-6 h-6 text-ink" />
            </div>
            <div>
              <h3 className="font-bold mb-2">Dedicated Team</h3>
              <p className="text-sm text-muted-foreground">
                Created and managed by football enthusiasts, researchers, editors, writers, and digital content creators
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-6 bg-muted/50 rounded-xl">
            <div className="p-3 bg-brand rounded-lg shrink-0">
              <Globe className="w-6 h-6 text-ink" />
            </div>
            <div>
              <h3 className="font-bold mb-2">Global Coverage</h3>
              <p className="text-sm text-muted-foreground">
                Providing football fans around the world with the latest match updates and news
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-6 bg-muted/50 rounded-xl">
            <div className="p-3 bg-brand rounded-lg shrink-0">
              <Award className="w-6 h-6 text-ink" />
            </div>
            <div>
              <h3 className="font-bold mb-2">Original Content</h3>
              <p className="text-sm text-muted-foreground">
                Independently collected, researched, reviewed, organized, and written by our editorial team
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 max-w-3xl">
          <div className="space-y-6 text-[15px] leading-relaxed text-foreground/90">
            <p>
              Weborbis.top is an independently operated football news and sports information platform created and managed by a dedicated team of football enthusiasts, researchers, editors, writers, and digital content creators.
            </p>

            <h2 className="text-2xl font-bold mt-8 flex items-center gap-3">
              <span className="w-1 h-8 bg-brand rounded-full"></span>
              Our Mission
            </h2>
            <p>
              Our mission is to provide football fans around the world with the latest match updates, football news, team information, player insights, competition analysis, and trending stories through accurate, accessible, and engaging editorial content.
            </p>

            <h2 className="text-2xl font-bold mt-8 flex items-center gap-3">
              <span className="w-1 h-8 bg-brand rounded-full"></span>
              What We Cover
            </h2>
            <p>
              Our platform focuses on covering a wide range of football-related topics, including international football competitions, club matches, national teams, player performances, match previews, post-match analysis, football trends, tactical discussions, transfer news, and other important events from the world of football.
            </p>

            <h2 className="text-2xl font-bold mt-8 flex items-center gap-3">
              <span className="w-1 h-8 bg-brand rounded-full"></span>
              Content Creation
            </h2>
            <p>
              Most of the content published on Weborbis.top — including match reports, football news articles, team analysis, player introductions, competition summaries, statistics interpretation, predictions, and editorial materials — is independently collected, researched, reviewed, organized, and written by our editorial team based on publicly available football information, official announcements, match data, and reliable sports sources.
            </p>
            <p>
              Our team continuously follows the latest developments in the football world, analyzes relevant information, and creates original editorial content to help readers better understand important matches, team performances, player conditions, and emerging football trends.
            </p>

            <h2 className="text-2xl font-bold mt-8 flex items-center gap-3">
              <span className="w-1 h-8 bg-brand rounded-full"></span>
              Editorial Standards
            </h2>
            <p>
              All football articles, analyses, reports, guides, recommendations, and informational materials available on Weborbis.top are original works created by our team for sports information, entertainment, and educational purposes.
            </p>
            <p>
              We continuously update and improve our content to maintain accuracy, originality, relevance, and a better reading experience for football fans.
            </p>

            <div className="mt-8 p-6 bg-muted/50 rounded-xl border border-border">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-5 h-5 text-brand-foreground" />
                <h3 className="font-bold">Intellectual Property & Disclaimers</h3>
              </div>
              <ul className="space-y-3 text-sm">
                <li>
                  Weborbis.top does not claim ownership of third-party football league names, club names, player names, trademarks, logos, images, videos, or other intellectual property referenced on our website. All trademarks, copyrights, and related assets belong to their respective owners.
                </li>
                <li>
                  We are not affiliated with FIFA, UEFA, Premier League, La Liga, Serie A, Bundesliga, Ligue 1, any football clubs, players, sports organizations, media companies, or official rights holders unless explicitly stated.
                </li>
                <li>
                  We do not use unauthorized APIs, private databases, leaked resources, restricted copyrighted materials, or exclusive sports content. Any referenced information is used only for news reporting, commentary, analysis, informational, editorial, or fair-use purposes where applicable.
                </li>
                <li>
                  Weborbis.top is intended for informational and entertainment purposes only. The content published on this website should not be considered professional sports betting advice, financial advice, or any guaranteed prediction of match results. Readers should make their own judgments based on available information.
                </li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold mt-8 flex items-center gap-3">
              <span className="w-1 h-8 bg-brand rounded-full"></span>
              Contact Us
            </h2>
            <p>
              For questions, business inquiries, collaborations, or copyright concerns, please contact:
            </p>
            <div className="mt-3 inline-flex items-center gap-2 px-4 py-3 bg-ink text-white rounded-lg">
              <Mail className="w-5 h-5" />
              <a href="mailto:support@weborbis.top" className="font-semibold hover:underline">support@weborbis.top</a>
            </div>
          </div>
        </div>

        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-brand/10 rounded-full border border-brand/30">
            <Newspaper className="w-5 h-5 text-brand-foreground" />
            <span className="font-semibold text-brand-foreground">Weborbis.top — Independent Football Coverage</span>
          </div>
        </div>
      </div>
    </PageShell>
  );
}